import React, { useEffect, useState, useRef } from"react";
import { useNavigate, useLocation } from"react-router-dom";
import axios from"axios";
import jsPDF from"jspdf";
import { useTenant } from"../../Context/TenantContext";
import API_BASE_URL from"../../Config/config";
import { checkClickStatus, recordButtonClick, hasUnlimitedClicks } from "../../utils/mockInterviewClickTracker";

import { useSubscription } from "../../Context/SubscriptionContext";
import FeatureGuard from "../access/FeatureGuard";
import useNexusLearnerBilling from "../../NextGenBuilder/hooks/useNexusLearnerBilling";
import NexusInterviewBlocked from "./NexusInterviewBlocked";

// Compiler Imports
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { cpp } from '@codemirror/lang-cpp';
import { java } from '@codemirror/lang-java';
import { oneDark } from '@codemirror/theme-one-dark';
import { javascript } from '@codemirror/lang-javascript';
import { 
  Play, 
  Terminal, 
  Trash2, 
  Code2,
  ChevronDown,
  Sparkles,
  Zap
} from 'lucide-react';

export default function CodingInterviewPage({ mode = "learner" }) {
    return (
        <FeatureGuard feature="MOCK_INTERVIEW">
            <CodingInterviewContent mode={mode} />
        </FeatureGuard>
    );
}

function CodingInterviewContent({ mode = "learner" }) {
 const navigate = useNavigate();
 const { state } = useLocation();
 const { getCollegeName, getLogoUrl, tenant } = useTenant();
 const formData = (state && state.formData) || {};

 const isTenantMode = mode === "tenant" || !!tenant;
 // NextGen Builder / TSG Nexus embeds this page under
 // /nextgen-builder/mock-interview — back navigation must stay inside
 // /nextgen-builder/*, never the college/tenant /student/* routes.
 const isNextGenBuilderRoute = window.location.pathname.startsWith('/nextgen-builder');
 const backPath = isNextGenBuilderRoute
   ? "/nextgen-builder/mock-interview"
   : (isTenantMode ? "/student/mockinterviewhome" : "/mockinterviewhome");
 const collegeName = getCollegeName();
 const collegeLogo = getLogoUrl();

 const [status, setStatus] = useState("idle");
 const [questions, setQuestions] = useState([]);
 const [currentIdx, setCurrentIdx] = useState(0);
 // TSG Nexus feature billing — inert outside /nextgen-builder/*.
 const nexus = useNexusLearnerBilling('MOCK_INTERVIEW');
 // Set only when the backend refuses to start the round (402). The offline
 // question fallback must NOT kick in for that — an exhausted quota is a
 // real answer, not a network failure.
 const [nexusBlocked, setNexusBlocked] = useState(null);
 const [userId, setUserId] = useState(() => {
   try {
     const userRaw = localStorage.getItem("user");
     const u = userRaw ? JSON.parse(userRaw) : null;
     return (u?.userId || "guest");
   } catch { return "guest"; }
 });

 // Compiler State
 const [code, setCode] = useState('// Write your solution here...\n\nfunction solution() {\n  // Start coding\n}\n');
 const [language, setLanguage] = useState('javascript');
 const [output, setOutput] = useState('');
 const [outputError, setOutputError] = useState('');
 const [loadingExec, setLoadingExec] = useState(false);
 const outputPanelRef = useRef(null);

 const languages = [
    { name: 'JavaScript', ext: 'javascript', mode: javascript, icon: '⚛️' },
    { name: 'Python', ext: 'python', mode: python, icon: '🐍' },
    { name: 'Java', ext: 'java', mode: java, icon: '☕' },
    { name: 'C++', ext: 'cpp', mode: cpp, icon: 'C++' },
    { name: 'C', ext: 'c', mode: cpp, icon: 'C' }
 ];

 const handleRunCode = async () => {
    setLoadingExec(true);
    setOutput('');
    setOutputError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/compiler/execute`, { code, language });
      setOutput(res.data.output || res.data.stdout || '');
      setOutputError(res.data.error || res.data.stderr || '');
      
      setTimeout(() => {
        outputPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } catch (err) {
      setOutputError(err.message || 'Error occurred');
    } finally {
      setLoadingExec(false);
    }
 };

 // Fetch questions when component mounts
 useEffect(() => {
   if (formData.skills && formData.jobDescription) {
     generateQuestions();
   } else {
     setStatus("idle");
     alert("Skills or job description missing. Please go back and fill out the form.");
   }
 }, []);

 const generateQuestions = async () => {
 setStatus("generating");
 setNexusBlocked(null);
 try {
 const res = await axios.post(`${API_BASE_URL}/ai-services`, {
 op: 'generate-questions',
 type:"coding",
 formData: {
 skills: formData.skills,
 jobDescription: formData.jobDescription,
 },
 numQuestions: 5,
 tenantId: tenant?.tenantId
 }, {
   headers: {
     "x-tenant-id": tenant?.tenantId || "",
     ...nexus.headers()
   }
 });

 if (!res.data.ok) throw new Error(res.data.error ||"Failed to generate questions");

 setQuestions(res.data.data || []);
 setCurrentIdx(0);
 setStatus("ready");
 nexus.noteUsage(res);
 } catch (err) {
 // TSG Nexus: out of free + paid interviews. Never start the round, and
 // never fall through to the offline fallback below.
 const body = (err && err.response && err.response.data) || {};
 if (nexus.isNexus && err && err.response && (err.response.status === 402 || body.code === 'MOCK_INTERVIEW_CREDITS_EXHAUSTED')) {
 if (!nexus.handleQuota(err)) nexus.openQuotaModal({ feature: 'MOCK_INTERVIEW', ...body });
 setNexusBlocked(body.message || null);
 setStatus("idle");
 return;
 }

 // Offline fallback: simple coding prompts
 const topic = formData.skills || formData.jobDescription || 'coding';
 const local = Array.from({ length: 5 }).map((_, i) => ({
 title: `Coding question #${i + 1}`,
 difficulty: ['Easy','Medium','Hard'][i % 3],
 problem_statement: `Implement a function related to ${topic}. Include edge cases and time complexity.`,
 sample_input: 'input',
 sample_output: 'output',
 explanation: 'Describe your approach and justify Big-O.',
 hints: ['Consider edge cases', 'Think two-pointer or hashing where applicable']
 }));
 setQuestions(local);
 setCurrentIdx(0);
 setStatus("ready");
 }
 };

 const downloadPDF = () => {
 const doc = new jsPDF();
 doc.setFontSize(16);
 doc.text("Coding Interview Questions", 10, 10);

 questions.forEach((q, idx) => {
 if (idx > 0) doc.addPage();

 doc.setFontSize(12);
 let y = 20;
 doc.text(`Q${idx + 1}: ${String(q.title ||"Untitled")}`, 10, y);
 y += 6;
 doc.setFontSize(10);
 doc.text(`Difficulty: ${String(q.difficulty ||"N/A")}`, 10, y);
 y += 8;
 doc.text("Problem Statement:", 10, y);
 y += 6;
 doc.text(String(q.problem_statement ||"No problem statement provided"), 10, y, { maxWidth: 190 });
 y += 30;

 doc.text("Sample Input:", 10, y);
 y += 6;
 doc.text(String(q.sample_input ||"No sample input provided"), 10, y, { maxWidth: 190 });
 y += 20;

 doc.text("Sample Output:", 10, y);
 y += 6;
 doc.text(String(q.sample_output ||"No sample output provided"), 10, y, { maxWidth: 190 });
 y += 20;

 doc.text("Explanation:", 10, y);
 y += 6;
 doc.text(String(q.explanation ||"No explanation provided"), 10, y, { maxWidth: 190 });
 y += 20;

 if (Array.isArray(q.hints) && q.hints.length > 0) {
 doc.text("Hints:", 10, y);
 y += 6;
 q.hints.forEach((hint) => {
 doc.text(`- ${String(hint ||"No hint")}`, 10, y);
 y += 6;
 });
 }
 });

 doc.save(`coding_questions_${Date.now()}.pdf`);
 };

 const renderSkills = () => {
 const skills = formData.skills;

 if (!skills) return <span>General Programming</span>;

 if (typeof skills ==="string") {
 return <span>{skills ||"None provided"}</span>;
 }

 if (Array.isArray(skills)) {
 if (skills.length === 0) return <span>No skills provided</span>;
 return <span>{skills.join(",")}</span>;
 }

 return <span>Unknown skill format</span>;
 };

 return (
 <div className="flex bg-bgMain text-body min-h-screen relative overflow-hidden font-sans">
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
 
 {/* Sidebar */}
 <div className="w-72 bg-bgSection p-8 border-r border-accentGray/30 flex flex-col h-screen relative z-10 shadow-2xl">
 <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-50"></div>
 
 {isTenantMode && collegeLogo ? (
   <div className="mb-6 flex justify-center">
     <img src={collegeLogo} alt={collegeName} className="h-12 object-contain" />
   </div>
 ) : (
   <h2 className="text-2xl font-black bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tighter mb-10">
     TheSkillGenie
   </h2>
 )}
 
 <div className="space-y-1 mb-8">
 <p className="text-[10px] font-black text-primary uppercase tracking-widest">{isTenantMode ? collegeName : "Target Skills"}</p>
 <div className="text-body font-extrabold text-sm leading-tight line-clamp-3">{renderSkills()}</div>
 </div>

 <div className="space-y-1 mb-10">
 <p className="text-[10px] font-black text-muted uppercase tracking-widest">Context (JD)</p>
 <div className="text-body/80 text-[10px] font-medium leading-relaxed line-clamp-4 italic">
 {formData.jobDescription ||"Standard Coding Round"}
 </div>
 </div>

 <p className="text-[10px] font-black text-muted uppercase tracking-widest mb-4">Question Set</p>
 <div className="grid grid-cols-5 gap-2 mb-10">
 {questions.map((_, idx) => (
 <button
 key={idx}
 onClick={() => setCurrentIdx(idx)}
 className={`w-10 h-10 rounded-xl text-xs font-black transition-all duration-200 ${
 currentIdx === idx
 ?"bg-primary text-white shadow-lg shadow-primary/30 scale-110"
 :"bg-bgMain text-muted border border-accentGray/30 hover:border-primary/50"
 }`}
 >
 {idx + 1}
 </button>
 ))}
 </div>

 <div className="mt-auto">
 {questions.length > 0 && (
 <button
 onClick={downloadPDF}
 className="w-full py-3 bg-primary/10 text-primary border border-primary/30 rounded-xl hover:bg-primary hover:text-white transition-all text-[10px] font-black uppercase tracking-widest shadow-lg"
 >
 ⬇️ Save as PDF
 </button>
 )}
 </div>
 </div>

 {/* Main Content */}
 <div className="flex-1 p-8 md:p-12 overflow-y-auto relative z-10">
 <div className="flex justify-between items-center mb-10">
 <button
 onClick={() => navigate(-1)}
 className="flex items-center gap-2 text-muted hover:text-white transition-colors group font-black uppercase text-[10px] tracking-widest"
 >
 <span className="w-8 h-8 rounded-full bg-bgSection flex items-center justify-center group-hover:bg-primary/20 transition-all">←</span>
 Back
 </button>
 <div className="px-6 py-2 bg-primary/10 text-primary border border-primary/30 rounded-full text-[10px] font-black uppercase tracking-widest">
 CODING ROUND • PHASE 1
 </div>
 </div>

 <div className="max-w-4xl mx-auto">
 {nexusBlocked !== null && status !=="generating" && (
 <div className="bg-bgSection rounded-[48px] border border-accentGray/30 shadow-2xl py-6">
 <NexusInterviewBlocked message={nexusBlocked} backPath={backPath} />
 </div>
 )}

 {nexusBlocked === null && status ==="idle" && (
 <div className="text-center py-20 bg-bgSection rounded-[48px] border border-accentGray/30 shadow-2xl">
 <span className="text-5xl mb-6 block">💻</span>
 <h2 className="text-3xl font-black text-black mb-4 uppercase tracking-tighter">Initialize Session</h2>
 <p className="text-muted mb-10 max-w-sm mx-auto">Ready to solve algorithmic challenges tailored to your career goals?</p>
 <button
 onClick={generateQuestions}
 className="btn-primary-gradient px-12 py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-105 active:scale-95 transition-all uppercase tracking-widest"
 >
 Generate Challenges
 </button>
 </div>
 )}

 {status ==="generating" && (
 <div className="text-center py-20">
 <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
 <p className="text-primary font-black uppercase tracking-widest">Assembling Algorithm Suite...</p>
 </div>
 )}

 {(status ==="ready" || status ==="done") && questions.length > 0 && (
 <div className="bg-bgSection p-8 md:p-12 rounded-[48px] shadow-2xl border border-accentGray/30 relative overflow-hidden">
 <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
 
 <div className="relative z-10">
 <div className="flex justify-between items-start mb-10">
 <div>
 <div className="text-xs font-black text-primary mb-2 uppercase tracking-widest">
 Challenge {currentIdx + 1} of {questions.length}
 </div>
 <h2 className="text-3xl md:text-4xl font-black text-black tracking-tighter leading-tight">
 {questions[currentIdx]?.title ||"Problem Statement"}
 </h2>
 </div>
 <span className={`px-4 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-widest shadow-lg ${
 questions[currentIdx]?.difficulty === 'Hard' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
 questions[currentIdx]?.difficulty === 'Medium' ? 'bg-secondary/10 text-primary border-secondary/30' :
 'bg-green-500/10 text-green-400 border-green-500/30'
 }`}>
 {questions[currentIdx]?.difficulty ||"Medium"}
 </span>
 </div>

 <div className="bg-bgSoft p-8 rounded-3xl border border-accentGray/30 mb-10">
 <p className="text-xl font-medium text-body leading-relaxed tracking-tight">
 {questions[currentIdx]?.problem_statement}
 </p>
 </div>

 <div className="grid md:grid-cols-2 gap-6 mb-10">
 <div className="space-y-3">
 <p className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Sample Input</p>
 <pre className="bg-bgMain p-6 rounded-2xl border border-accentGray/30 font-mono text-sm text-primary shadow-inner overflow-auto">
 {questions[currentIdx]?.sample_input ||"No specific input required"}
 </pre>
 </div>
 <div className="space-y-3">
 <p className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Expected Output</p>
 <pre className="bg-bgMain p-6 rounded-2xl border border-accentGray/30 font-mono text-sm text-green-400 shadow-inner overflow-auto">
 {questions[currentIdx]?.sample_output}
 </pre>
 </div>
 </div>

 <div className="space-y-6 pt-10 border-t border-accentGray/30">
    {/* Hints Section */}
    <div className="space-y-3">
        <p className="text-[10px] font-black text-primary uppercase tracking-widest ml-1">Pro Hints</p>
        <ul className="flex flex-wrap gap-3">
        {(Array.isArray(questions[currentIdx]?.hints) ? questions[currentIdx].hints : []).map((h, i) => (
        <li key={i} className="px-4 py-2 bg-bgSection border border-primary/20 rounded-xl text-xs font-bold text-primary flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
        {h}
        </li>
        ))}
        </ul>
    </div>
 </div>

 {/* Compiler Integration */}
 <div className="mt-12 space-y-6">
    <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-xl text-primary">
                <Code2 size={20} />
            </div>
            <h3 className="text-xl font-black text-black uppercase tracking-tight">Code Compiler</h3>
        </div>
        
        <div className="flex items-center gap-4">
            <select
                className="bg-bgMain border border-accentGray/30 rounded-xl px-4 py-2 text-sm font-bold focus:outline-none"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
            >
                {languages.map((lang) => (
                  <option key={lang.ext} value={lang.ext}>{lang.name}</option>
                ))}
            </select>

            <button
                onClick={handleRunCode}
                disabled={loadingExec}
                className="flex items-center gap-2 px-6 py-2 btn-primary-gradient rounded-xl font-bold shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
            >
                {loadingExec ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Play size={16} fill="currentColor" />}
                <span>Run Code</span>
            </button>
        </div>
    </div>

    <div className="grid lg:grid-cols-2 gap-6 min-h-[500px]">
        {/* Editor */}
        <div className="bg-[#1e1e1e] rounded-[32px] border border-accentGray/20 overflow-hidden shadow-2xl flex flex-col">
            <div className="px-6 py-3 border-b border-white/10 flex justify-between items-center bg-[#252525]">
                <span className="text-[10px] font-black text-muted uppercase tracking-widest">Main.{language}</span>
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/50" />
                </div>
            </div>
            <div className="flex-1 overflow-hidden">
                <CodeMirror
                    value={code}
                    height="100%"
                    extensions={language ? [languages.find((l) => l.ext === language)?.mode?.()] : []}
                    theme={oneDark}
                    onChange={(val) => setCode(val)}
                    className="h-full text-base font-mono"
                />
            </div>
        </div>

        {/* Output Side */}
        <div className="flex flex-col gap-6">
            <div className="flex-1 bg-bgMain rounded-[32px] border border-accentGray/30 overflow-hidden flex flex-col shadow-inner">
                <div className="px-6 py-4 border-b border-accentGray/30 flex justify-between items-center bg-bgSoft">
                    <div className="flex items-center gap-2">
                        <Terminal size={16} className="text-primary" />
                        <span className="text-[10px] font-black text-black uppercase tracking-widest">Output Terminal</span>
                    </div>
                    {(output || outputError) && (
                        <button 
                            onClick={() => { setOutput(''); setOutputError(''); }}
                            className="text-[10px] font-black text-red-500 uppercase hover:underline"
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div className="p-8 font-mono text-sm overflow-y-auto flex-1 bg-[#0a0a0a]" ref={outputPanelRef}>
                    {!output && !outputError ? (
                        <div className="h-full flex flex-col items-center justify-center text-muted opacity-30 gap-4">
                            <Zap size={48} />
                            <p className="font-bold">Run code to see output...</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {outputError && (
                                <div className="text-red-400 whitespace-pre-wrap p-4 bg-red-500/10 rounded-2xl border border-red-500/20">
                                    {outputError}
                                </div>
                            )}
                            {output && (
                                <div className="text-emerald-400 whitespace-pre-wrap">
                                    {output}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
 </div>

 {/* Navigation */}
 <div className="flex justify-between items-center mt-12 pt-10 border-t border-accentGray/30">
 <button
 onClick={() => setCurrentIdx((prev) => Math.max(prev - 1, 0))}
 disabled={currentIdx === 0}
 className="text-muted font-black uppercase text-xs tracking-widest hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
 >
 ← Previous
 </button>
 <button
 onClick={() => setCurrentIdx((prev) => Math.min(prev + 1, questions.length - 1))}
 disabled={currentIdx === questions.length - 1}
 className={`px-10 py-4 rounded-2xl font-black transition-all shadow-xl uppercase tracking-widest ${
 currentIdx === questions.length - 1
 ?"bg-bgMain text-muted border border-accentGray/30 opacity-50 cursor-not-allowed"
 :"bg-primary text-white hover:bg-primary/90 hover:scale-105"
 }`}
 >
 Next Challenge →
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}


